import jwtDecode from 'jwt-decode';
import { createSelector } from 'reselect';
import { UserRoleType, AuthType } from '../enums';
const slice = (state) => state.auth;
export const selectToken = (state) => slice(state).token;
export const selectSsoToken = (state) => slice(state).ssoToken;
export const selectAuthType = (state) => slice(state).tenantAuth.type;
export const selectAuthLoading = (state) => slice(state).tenantAuth.loading;
export function makeSelectDecoddedToken(rawTokenSelector) {
    return createSelector(rawTokenSelector, token => {
        if (typeof token !== 'string') {
            return null;
        }
        try {
            return jwtDecode(token);
        }
        catch (e) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Failed to decode JWT', e); // eslint-disable-line
            }
            return null;
        }
    });
}
export const selectDecodedToken = makeSelectDecoddedToken(selectToken);
export const selectDecodedSsoToken = makeSelectDecoddedToken(selectSsoToken);
export const selectUserId = createSelector(selectDecodedToken, jwt => { var _a; return (_a = jwt === null || jwt === void 0 ? void 0 : jwt.user_name) !== null && _a !== void 0 ? _a : ''; });
export const selectIdpUserId = createSelector(selectDecodedToken, jwt => { var _a; return (_a = jwt === null || jwt === void 0 ? void 0 : jwt.idp_user_id) !== null && _a !== void 0 ? _a : ''; });
export const selectCredentialsId = createSelector(selectDecodedToken, jwt => { var _a; return (_a = jwt === null || jwt === void 0 ? void 0 : jwt.credentials_id) !== null && _a !== void 0 ? _a : ''; });
export const selectClientDomain = createSelector(selectDecodedToken, jwt => { var _a; return (_a = jwt === null || jwt === void 0 ? void 0 : jwt.client_domain) !== null && _a !== void 0 ? _a : ''; });
/**
 * Returns tenants field from SSO token parsed as array.
 */
export const selectMultiTenants = createSelector(selectDecodedSsoToken, ssoToken => { var _a, _b; return (_b = (_a = ssoToken === null || ssoToken === void 0 ? void 0 : ssoToken.tenants) === null || _a === void 0 ? void 0 : _a.split(',')) !== null && _b !== void 0 ? _b : []; });
export const selectLoggedIn = createSelector(selectDecodedToken, jwt => {
    if (jwt === null)
        return false;
    const now = Date.now().valueOf() / 1000;
    const expired = now > jwt.exp;
    return !expired;
});
export const selectUserRoles = createSelector(selectDecodedToken, jwt => { var _a; return (_a = jwt === null || jwt === void 0 ? void 0 : jwt.authorities) !== null && _a !== void 0 ? _a : []; });
export const selectUserName = createSelector(selectDecodedSsoToken, selectIdpUserId, selectUserId, selectUserRoles, (jwt, idpUserId, userId, roles) => {
    if (idpUserId) {
        return idpUserId;
    }
    const isNotOwner = roles.includes(UserRoleType.Member) ||
        roles.includes(UserRoleType.Admin);
    const trimEmailPart = (t) => t.split('@')[0];
    if (isNotOwner &&
        !!(jwt === null || jwt === void 0 ? void 0 : jwt.idp_user_id) &&
        (jwt.auth_type === AuthType.Saml || jwt.auth_type === AuthType.Ldap)) {
        return trimEmailPart(jwt.idp_user_id);
    }
    return isNotOwner ? trimEmailPart(userId) : userId;
});
export const selectIsAdmin = createSelector(selectUserRoles, roles => roles.includes(UserRoleType.Admin));
export const selectIsSamlUser = createSelector(selectDecodedSsoToken, jwt => (jwt === null || jwt === void 0 ? void 0 : jwt.auth_type) === AuthType.Saml);
export const selectIsLdapUser = createSelector(selectDecodedSsoToken, jwt => (jwt === null || jwt === void 0 ? void 0 : jwt.auth_type) === AuthType.Ldap);
